import * as React from 'react';
import { useRef, useState } from 'react';
import classnames from '../../../core/utils/classnames.tsx';
import { format, isValid, parse } from 'date-fns';
import {
    isoDateFormat,
    isoDateTimeFormat,
    isoDateTimeFormatWithMcsAndTimezone,
    isoDateTimeFormatWithMs,
    isoDateTimeFormatWithMsAndTimezone,
    isoDateTimeFormatWithTimezone,
    isoTimeFormat,
} from '../../../core/utils/dates.ts';
import { getLocalizedDetail } from '../../../core/utils/localization.ts';

const SUPPORTED_DATE_FORMATS = [
    isoDateFormat,
    isoDateTimeFormat,
    isoDateTimeFormatWithTimezone,
    isoTimeFormat,
    isoDateTimeFormatWithMs,
    isoDateTimeFormatWithMsAndTimezone,
    isoDateTimeFormatWithTimezone,
    isoDateTimeFormatWithMcsAndTimezone,
];

const ICON_HOVER_DELAY = 250; // ms

export const localizeExplanationDetails = (t: any, explanationDetails: any, dateFormat: string = 'L', i18n: any) => {
    if (!explanationDetails) return explanationDetails;

    const dateFieldNames = ['previous_field_value', 'field_value', 'expected_value', 'obtained_value', 'time_stamp'];
    for (const dateFieldName of dateFieldNames) {
        if (explanationDetails?.[dateFieldName]) {
            // Try each supported format
            for (const supportedFormat of SUPPORTED_DATE_FORMATS) {
                try {
                    const parsedDate = parse(explanationDetails[dateFieldName], supportedFormat, new Date());

                    if (isValid(parsedDate)) {
                        explanationDetails[dateFieldName] = format(parsedDate, dateFormat);
                        break; // Stop trying formats once we find a valid one
                    }
                } catch (e) {
                    // Continue to next format
                }
            }
        }
    }

    explanationDetails['threshold_unit'] =
        explanationDetails?.threshold_matching === 'date' ? t(`assistance:explanation.threshold_unit_days`) : '';

    if (i18n.exists(`assistance:explanation.${explanationDetails?.document_matching_name}`)) {
        explanationDetails['document_matching_name'] = t(
            `assistance:explanation.${explanationDetails?.document_matching_name}`
        );
    }

    if (explanationDetails?.text_en !== undefined) {
        explanationDetails['text'] = getLocalizedDetail(explanationDetails, 'text');
    }

    return explanationDetails;
};

export const legacyExplanationDetails = (translationKey: string, explanationDetails: any): [string, any] => {
    // Basically we changed a translation so the old values wouldn't be found anymore
    // If you add more cases please make sure to add the date as well so we can remove it later

    // 2024-07-04: split between multiple_good_results and multiple_good_article_results
    if (translationKey === 'explanation.multiple_good_results' && explanationDetails?.article_number1) {
        return ['explanation.multiple_good_article_results', explanationDetails];
    }

    // 2025-03-21: added table to confidence explanation
    if (translationKey === 'explanation.historical_matches' && explanationDetails?.mapping_search_fields) {
        return ['explanation.historical_matches_with_table', explanationDetails];
    }

    return [translationKey, explanationDetails];
};

export const useExplanationVisibility = ({ wrapperRef, isFocusActive }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMouseOver, setIsMouseOver] = useState(false);
    const [alignment, setAlignment] = useState<'start' | 'center' | 'end'>('start');

    const iconMouseEnterDelayTimeoutRef = useRef(null);

    const updateAlignment = () => {
        const wrapperRect = wrapperRef.current?.getBoundingClientRect();
        const formGroup = wrapperRef.current?.closest('.js-form-item-group');
        const formGroupRect = formGroup?.getBoundingClientRect();
        if (!wrapperRect || !formGroupRect) return;
        // distance to the left and right INCLUDING the width of the current field
        const spaceLeft = wrapperRect?.right - formGroupRect?.left;
        const spaceRight = formGroupRect?.left + formGroup?.scrollWidth - wrapperRect?.left;

        if (spaceRight >= 450) {
            setAlignment('start');
        } else if (spaceRight < 450 && spaceLeft > 450) {
            setAlignment('end');
        } else {
            setAlignment('center');
        }
    };

    const handleFocus = (e) => {
        setIsOpen(true);
        updateAlignment();
    };

    const handleBlur = (e) => {
        if (!isMouseOver) {
            setIsOpen(false);
        }
    };

    const handleIconMouseEnter = (e) => {
        iconMouseEnterDelayTimeoutRef.current = setTimeout(() => {
            setIsOpen(true);
            updateAlignment();
        }, ICON_HOVER_DELAY);
    };

    const handleMouseEnter = (e) => {
        setIsMouseOver(true);
    };

    const handleIconMouseLeave = (e) => {
        if (iconMouseEnterDelayTimeoutRef.current) {
            clearTimeout(iconMouseEnterDelayTimeoutRef.current);
        }
    };

    const handleMouseLeave = (e) => {
        setIsMouseOver(false);
        if (!isFocusActive) {
            // keep explanation open while cursor moves around
            setIsOpen(false);
        }
    };

    const handleMouseOut = (e) => {
        // this can be bubbled from children
        setIsMouseOver(false);
    };

    return {
        isOpen,
        alignment,
        onIsOpenChange: setIsOpen,
        onFieldFocus: handleFocus,
        onFieldBlur: handleBlur,
        onIconMouseEnter: handleIconMouseEnter,
        onIconMouseLeave: handleIconMouseLeave,
        onFieldMouseEnter: handleMouseEnter,
        onFieldMouseLeave: handleMouseLeave,
        onFieldMouseOut: handleMouseOut,
    };
};

const Explanation = ({ className, severity, ...props }: React.ComponentPropsWithRef<'div'> & { severity?: string }) => {
    return (
        <div
            className={classnames(
                'text-xs leading-relaxed break-words mt-1 p-2 text-content-strong bg-surface-strong border border-line-strong border-solid rounded w-full flex-1 flex flex-col gap-3',

                severity === 'error' && 'text-content-danger-strong bg-surface-danger-weak border-line-danger-strong',
                severity === 'warning' &&
                    'text-content-warning-strong bg-surface-warning-weak border-line-warning-strong',
                severity === 'success' &&
                    'text-content-success-strong bg-surface-success-weak border-line-success-strong',

                className
            )}
            {...props}
        />
    );
};

export default Explanation;
